import {
  BaseBoxShapeUtil,
  Editor,
  FileHelpers,
  HTMLContainer,
  MediaHelpers,
  RecordProps,
  T,
  TLAssetId,
  TLBaseShape,
  TLImageShapeProps,
  TLResizeInfo,
  TLShapePartial,
  Vec,
  lerp,
  resizeBox,
  structuredClone,
  toDomPrecision,
  useValue,
} from "@tldraw/editor";
import classNames from "classnames";
import React, { memo, useEffect, useState } from "react";
import { useImageOrVideoAsset } from "./shared/useImageOrVideoAsset";
import { usePrefersReducedMotion } from "./shared/usePrefersReducedMotion";
import { BrokenAssetIcon } from "./shared/BrokenAssetIcon";
import { HyperlinkButton } from "./shared/HyperlinkButton";
import { mixColors } from "../CustomCloudUtil";
import { ShapeUtilHelper } from "../ShapeUtilHelper";
import { areCornersUniform, cornerRadiiCss, getCornerRadii, roundedRectPath } from "../utils/cornerRadii";
import { shadowsToCssBoxShadow, shadowsToCssFilterDropShadow } from "../shadowUtils";
import { HOVER_EFFECT } from "../constants";
import { useStore } from "../../../../../../../store";
import { StoreType } from "../../../../../../../store/types";

async function getDataURIFromURL(url: string): Promise<string> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return FileHelpers.blobToDataUrl(blob);
  } catch (error) {
    console.error(error);
    return "";
  }
}

export type CustomImageUtilProps = TLBaseShape<
  "customImage",
  {
    w: number;
    h: number;
    assetId: string | null;
    playing: boolean;
    url: string;
    crop: any;
    flipX: boolean;
    flipY: boolean;
    outlineColor: string;
    fill: string;
    dash: "solid" | "dashed";
    transparency: number;
    qrCodeLink?: string; // Add QR code link property that persists with the shape
    strokeDasharray?: string;
    strokeWidth?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    rounded?: number;
    // Optional per-corner overrides for `rounded`. When undefined the
    // corner falls back to the uniform `rounded` value. Edited via the
    // CornerRadiusPopover in the Alignment panel.
    roundedTL?: number;
    roundedTR?: number;
    roundedBR?: number;
    roundedBL?: number;
    filterBlur?: number;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    [key: string]: any;
  }
>;

export class CustomImageUtil extends BaseBoxShapeUtil<CustomImageUtilProps> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: Editor) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static type = "customImage" as const;
  static props: any;

  isAspectRatioLocked() {
    return true;
  }

  canCrop() {
    return true;
  }

  // Disable resize for mask shapes
  canResize(shape: CustomImageUtilProps) {
    if (shape.meta?.isMaskShape) {
      return false;
    }
    return true; // Default: allow resize for normal images
  }

  // Override onDoubleClick to disable crop mode on double-click
  onDoubleClick(_shape: CustomImageUtilProps) {
    return;
  }

  getDefaultProps(): CustomImageUtilProps["props"] {
    return {
      w: 100,
      h: 100,
      assetId: null,
      playing: true,
      url: "",
      crop: null,
      flipX: false,
      flipY: false,
      outlineColor: "#000000",
      fill: "#e3e3e3",
      dash: "solid",
      transparency: 1,
      strokeWidth: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      rounded: 0,
      filterBlur: 0,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {},
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
    };
  }

  onResize(shape: CustomImageUtilProps, info: TLResizeInfo<CustomImageUtilProps>) {
    let resized: CustomImageUtilProps = resizeBox(shape, info);
    const { flipX, flipY } = info.initialShape.props;
    const { scaleX, scaleY, mode } = info;

    resized = {
      ...resized,
      props: {
        ...resized.props,
        flipX: scaleX < 0 !== flipX,
        flipY: scaleY < 0 !== flipY,
      },
    };
    if (!shape.props.crop) return resized;

    const flipCropHorizontally =
      // We used the flip horizontally feature
      (mode === "scale_shape" && scaleX === -1) ||
      // We resized the shape past it's bounds, so it flipped
      (mode === "resize_bounds" && flipX !== resized.props.flipX);
    const flipCropVertically =
      // We used the flip vertically feature
      (mode === "scale_shape" && scaleY === -1) ||
      // We resized the shape past it's bounds, so it flipped
      (mode === "resize_bounds" && flipY !== resized.props.flipY);

    const { topLeft, bottomRight } = shape.props.crop;
    resized.props.crop = {
      topLeft: {
        x: flipCropHorizontally ? 1 - bottomRight.x : topLeft.x,
        y: flipCropVertically ? 1 - bottomRight.y : topLeft.y,
      },
      bottomRight: {
        x: flipCropHorizontally ? 1 - topLeft.x : bottomRight.x,
        y: flipCropVertically ? 1 - topLeft.y : bottomRight.y,
      },
    };
    return resized;
  }

  hideSelectionBoundsFg(_shape: CustomImageUtilProps): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: CustomImageUtilProps): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: CustomImageUtilProps): boolean {
    return false; // Always show resize handles regardless of opacity
  }

  component(shape: CustomImageUtilProps) {
    // Subscribe only to the *boolean* "is this shape hovered" instead of the
    // full `getHoveredShape()` record. See CustomRectUtil for the rationale —
    // avoids O(N) re-renders on every hover/pan/zoom transition.
    const editor = this.editor;
    const isHovered = useValue("isHovered", () => editor.getHoveredShapeId() === shape.id, [editor, shape.id]);

    const isAnimated = getIsAnimated(this.editor, shape);
    return <ImageShape shape={shape} isAnimated={isAnimated} editor={this.editor} isHovered={isHovered} />;
  }

  addDefaultColorMix(shape: CustomImageUtilProps) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: CustomImageUtilProps) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: CustomImageUtilProps) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  indicator(shape: CustomImageUtilProps) {
    const isCropping = this.editor.getCroppingShapeId() === shape.id;
    if (isCropping) return null;

    const cornerRadii = getCornerRadii(shape.props);
    const cornersUniform = areCornersUniform(cornerRadii);
    const w = toDomPrecision(shape.props.w);
    const h = toDomPrecision(shape.props.h);

    // ALWAYS render indicator, ignore isShowIndicator prop.
    // Falls back to a `<path>` when corners differ so the dashed
    // selection outline tracks the actual shape rather than a single
    // uniform radius.
    if (!cornersUniform) {
      return (
        <path
          d={roundedRectPath(0, 0, w, h, cornerRadii)}
          stroke="#338bff"
          strokeWidth="2"
          fill="none"
          opacity="1"
          style={{
            stroke: "#338bff",
            strokeWidth: 2,
            fill: "none",
            opacity: 1,
          }}
        />
      );
    }
    return (
      <rect
        x={0}
        y={0}
        rx={shape.props.rounded}
        ry={shape.props.rounded}
        width={w}
        height={h}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }

  async toSvg(shape: CustomImageUtilProps) {
    if (!shape.props.assetId) return null;

    const asset = this.editor.getAsset(shape.props.assetId as TLAssetId);

    if (!asset) return null;

    let src = await this.editor.resolveAssetUrl(shape.props.assetId as TLAssetId, {
      shouldResolveToOriginal: true,
    });
    if (!src) return null;
    if (src.startsWith("blob:") || src.startsWith("http") || src.startsWith("/") || src.startsWith("./")) {
      // If it's a remote image, we need to fetch it and convert it to a data URI
      src = (await getDataURIFromURL(src)) || "";
    }

    return <SvgImage shape={shape} src={src} />;
  }

  onDoubleClickEdge(shape: CustomImageUtilProps) {
    const props = shape.props;
    if (!props) return;

    if (this.editor.getCroppingShapeId() !== shape.id) {
      return;
    }

    const crop = structuredClone(props.crop) || {
      topLeft: { x: 0, y: 0 },
      bottomRight: { x: 1, y: 1 },
    };

    // The true asset dimensions
    const w = (1 / (crop.bottomRight.x - crop.topLeft.x)) * shape.props.w;
    const h = (1 / (crop.bottomRight.y - crop.topLeft.y)) * shape.props.h;

    const pointDelta = new Vec(crop.topLeft.x * w, crop.topLeft.y * h).rot(shape.rotation);

    const partial: TLShapePartial<CustomImageUtilProps> = {
      id: shape.id,
      type: shape.type,
      x: shape.x - pointDelta.x,
      y: shape.y - pointDelta.y,
      props: {
        crop: {
          topLeft: { x: 0, y: 0 },
          bottomRight: { x: 1, y: 1 },
        },
        w,
        h,
      },
    };

    this.editor.updateShapes([partial]);
  }
  getInterpolatedProps(startShape: CustomImageUtilProps, endShape: CustomImageUtilProps, t: number): any {
    function interpolateCrop(
      startShape: CustomImageUtilProps,
      endShape: CustomImageUtilProps
    ): TLImageShapeProps["crop"] {
      if (startShape.props.crop === null && endShape.props.crop === null) return null;

      const startTL = startShape.props.crop?.topLeft || { x: 0, y: 0 };
      const startBR = startShape.props.crop?.bottomRight || { x: 1, y: 1 };
      const endTL = endShape.props.crop?.topLeft || { x: 0, y: 0 };
      const endBR = endShape.props.crop?.bottomRight || { x: 1, y: 1 };

      return {
        topLeft: { x: lerp(startTL.x, endTL.x, t), y: lerp(startTL.y, endTL.y, t) },
        bottomRight: { x: lerp(startBR.x, endBR.x, t), y: lerp(startBR.y, endBR.y, t) },
      };
    }

    return {
      ...(t > 0.5 ? endShape.props : startShape.props),
      w: lerp(startShape.props.w, endShape.props.w, t),
      h: lerp(startShape.props.h, endShape.props.h, t),
      crop: interpolateCrop(startShape, endShape),
    };
  }
}

const ImageShape = memo(function ImageShape({
  shape,
  isAnimated,
  editor,
  isHovered,
}: {
  shape: CustomImageUtilProps;
  isAnimated: boolean;
  editor: Editor;
  isHovered: boolean;
}) {
  const { asset, url } = useImageOrVideoAsset({
    shapeId: shape.id,
    assetId: shape.props.assetId as TLAssetId,
    editor,
  });

  // Access store to get QR code link for data attributes (fallback)
  const {
    campaign: { qrCodeLink: campaignQrCodeLink },
  }: StoreType = useStore();

  // Detect if this is a QR code shape
  const isQRCodeShape =
    shape.id === "shape:qrCode" ||
    shape.id.includes("qrCode") ||
    (shape.props.assetId && shape.props.assetId.toString().includes("qr"));

  // Get QR link from shape properties first, then fallback to campaign store
  const effectiveQrLink = isQRCodeShape ? shape.props.qrCodeLink || campaignQrCodeLink || "" : "";

  // Create QR code data attributes if this is a QR code shape
  const qrCodeAttributes = isQRCodeShape
    ? {
        "data-qr-code": "true",
        "data-qr-link": effectiveQrLink,
        id: `qr-code-${shape.id}`,
        "data-shape-id": shape.id,
      }
    : {};

  const {
    fill,
    outlineColor,
    transparency,
    borderWidth,
    borderColor,
    borderSolid,
    isShowBorderTop,
    isShowBorderBottom,
    isShowBorderLeft,
    isShowBorderRight,
    rounded,
    filterBlur,
    isCreatedFilterBlur,
    filterBlurValueWhenHidden,
    outShadowArray,
    inShadowArray,
    colorMix,
    outlineColorMix,
    isHiddenLayerBlur,
    isHiddenBorder,
  } = shape.props;

  // CSS `border-radius` used by the four nested wrappers below. When
  // the user hasn't edited individual corners the helpers collapse to
  // the same uniform value as before; once a corner override is set,
  // every wrapper receives the 4-value form so the rounded silhouette
  // is consistent through the image, the fill overlay, and the QR /
  // hover affordances.
  const cornerRadii = getCornerRadii(shape.props);
  const radiusCss = areCornersUniform(cornerRadii) ? `${cornerRadii.tl}px` : cornerRadiiCss(cornerRadii);

  const visibleColors = Object.values(colorMix).filter((c) => !c.isHidden);
  const calculatedFill = visibleColors.length > 0 ? mixColors(visibleColors.map((c) => c.color)) : "transparent";

  const visibleOutlineColors = Object.values(outlineColorMix).filter((c) => !c.isHidden);
  const calculatedOutlineColor =
    visibleOutlineColors.length > 0 ? mixColors(visibleOutlineColors.map((c) => c.color)) : "transparent";

  // Check if this is a mask shape with original image dimensions
  const isMaskShape = (shape.meta?.isMaskShape && shape.meta?.isImageMask) || shape.meta?.isCompositePart;
  const isPreviewShape = shape.meta?.isPreviewShape === true;
  const imageOffsetX = shape.meta?.imageOffsetX as number | undefined;
  const imageOffsetY = shape.meta?.imageOffsetY as number | undefined;
  const originalImageWidth = shape.meta?.originalImageWidth as number | undefined;
  const originalImageHeight = shape.meta?.originalImageHeight as number | undefined;

  // Calculate offset if this is a masked image - use stored CONSTANT offset
  // EXCEPT for preview shapes (Union/Exclude) which scale the image to fit result bounds
  const imageOffset =
    isMaskShape &&
    !isPreviewShape &&
    originalImageWidth &&
    originalImageHeight &&
    imageOffsetX !== undefined &&
    imageOffsetY !== undefined
      ? {
          offsetX: imageOffsetX, // Use CONSTANT offset from meta (never changes)
          offsetY: imageOffsetY, // Use CONSTANT offset from meta (never changes)
          originalWidth: originalImageWidth,
          originalHeight: originalImageHeight,
        }
      : null;

  const prefersReducedMotion = usePrefersReducedMotion();
  const [staticFrameSrc, setStaticFrameSrc] = useState("");
  const [loadedUrl, setLoadedUrl] = useState<null | string>(null);
  const [shadows, setShadows] = useState({ outShadows: "", inShadows: "" });

  useEffect(() => {
    if (url && isAnimated) {
      let cancelled = false;

      const image = new Image();
      image.onload = () => {
        if (cancelled) return;

        const canvas = document.createElement("canvas");
        canvas.width = image.width;
        canvas.height = image.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.drawImage(image, 0, 0);
        setStaticFrameSrc(canvas.toDataURL());
        setLoadedUrl(url);
      };
      image.crossOrigin = "anonymous";
      image.src = url;

      return () => {
        cancelled = true;
      };
    }
  }, [editor, isAnimated, prefersReducedMotion, url]);

  const showCropPreview = useValue(
    "show crop preview",
    () =>
      shape.id === editor.getOnlySelectedShapeId() &&
      editor.getCroppingShapeId() === shape.id &&
      editor.isIn("select.crop"),
    [editor, shape.id]
  );

  // We only want to reduce motion for mimeTypes that have motion
  const reduceMotion = prefersReducedMotion && (asset?.props.mimeType?.includes("video") || isAnimated);

  const containerStyle =
    isMaskShape && imageOffset
      ? {
          // For image masks/composite parts, use the ORIGINAL image dimensions (don't scale)
          width: imageOffset.originalWidth,
          height: imageOffset.originalHeight,
        }
      : {
          // For normal images (and preview shapes), use getCroppedContainerStyle or shape dimensions
          ...getCroppedContainerStyle(shape),
        };

  const nextSrc = url === loadedUrl ? null : url;
  const loadedSrc = reduceMotion ? staticFrameSrc : loadedUrl;

  const mixedColor = mixColors(
    Object.values(colorMix)
      .filter((colorObj) => !colorObj.isHidden)
      .map((colorObj) => colorObj.color)
  );

  // This logic path is for when it's broken/missing asset.
  if (!url && !asset?.props.src) {
    return (
      <HTMLContainer
        id={shape.id}
        style={{
          backgroundColor: "transparent",
          overflow: "hidden",
          width: shape.props.w,
          height: shape.props.h,
          opacity: transparency,
        }}
        key={`${JSON.stringify(outShadowArray)}-${JSON.stringify(inShadowArray)}`}
      >
        <div className={classNames("tl-image-container", asset && "tl-image-container-loading")} style={containerStyle}>
          {asset ? null : <BrokenAssetIcon />}
        </div>
        {"url" in shape.props && shape.props.url && <HyperlinkButton url={shape.props.url} />}
      </HTMLContainer>
    );
  }

  // We don't set crossOrigin for non-animated images because for Cloudflare we don't currently
  // have that set up.
  const crossOrigin = isAnimated ? "anonymous" : undefined;

  useEffect(() => {
    const outShadows = shadowsToCssBoxShadow(outShadowArray as any, undefined);
    const inShadows = shadowsToCssBoxShadow(undefined, inShadowArray as any);
    setShadows({ outShadows, inShadows });
  }, [outShadowArray, inShadowArray]);

  // For QR codes (transparent SVG content) box-shadow would draw around the
  // rectangular bounding box and also get clipped by overflow: hidden on the
  // outer wrapper. We use CSS `filter: drop-shadow(...)` on the outermost
  // element instead — it follows the actual opaque pixels of the QR.
  const qrDropShadowFilter = isQRCodeShape
    ? shadowsToCssFilterDropShadow(outShadowArray as any) || undefined
    : undefined;

  // Optimistic QR pixel-color tint. Changing a QR's color requires
  // regenerating its whole SVG asset (async + throttled), so the <img>
  // lags by ~50ms+ behind the picker. To make the color feel instant, we
  // overlay a div that uses the QR's module geometry as an alpha mask and
  // fills it with `qrPixelColor` straight from the shape prop (updated
  // synchronously by the color picker). The module geometry depends only on
  // the QR content (not the color), so the current SVG is always a valid
  // mask while only the color is changing.
  //
  // The qrcode lib emits the background as the single `fill="..."` path and
  // the modules as a `stroke="..."` path. We force that background fill to
  // `none` so the alpha mask covers ONLY the modules — this makes the tint
  // work even for QRs with an opaque (e.g. white) background, where the
  // unmodified SVG would otherwise be fully opaque and tint the whole square.
  const qrPixelColor = (shape.props as any).qrPixelColor || "#000000";
  // Plain const (not useMemo): a hook here would sit after the broken-asset
  // early return above and risk a changing hook count. The work is trivial.
  const qrTintMaskSrc = (() => {
    if (!isQRCodeShape || !loadedSrc) return null;
    const SVG_DATA_PREFIX = "data:image/svg+xml;base64,";
    if (loadedSrc.startsWith(SVG_DATA_PREFIX)) {
      try {
        const svg = atob(loadedSrc.slice(SVG_DATA_PREFIX.length));
        // Only the background path carries `fill`; modules use `stroke`.
        const maskSvg = svg.replace(/fill="[^"]*"/, 'fill="none"');
        return `${SVG_DATA_PREFIX}${btoa(maskSvg)}`;
      } catch {
        // Fall through to using the raw asset as the mask.
      }
    }
    // Non-SVG / remote asset: only safe to mask directly when the background
    // is already transparent (otherwise the whole square would be tinted).
    const hasOpaqueBg =
      Boolean((shape.props as any).isBackgroundVisible) && Boolean((shape.props as any).qrBackgroundColor);
    return hasOpaqueBg ? null : loadedSrc;
  })();
  const showQrColorTint = isQRCodeShape && !!qrTintMaskSrc;

  // Optimistic QR background tint. The background is the whole square behind the
  // modules, so a plain solid fill (no mask) is enough. It's drawn BEHIND the
  // module tint overlay below, and only when the module tint is also shown so
  // the modules always paint on top of it (never a solid square hiding them).
  const qrBackgroundColor = (shape.props as any).qrBackgroundColor || "";
  const qrBackgroundVisible = Boolean((shape.props as any).isBackgroundVisible) && !!qrBackgroundColor;
  const showQrBgTint = showQrColorTint && qrBackgroundVisible;

  return (
    <div
      className="tl-shape"
      data-shape-type={isQRCodeShape ? "qrCode" : "customImage"}
      data-shape-is-filled="true"
      data-shape-id={shape.id}
      draggable="false"
      style={{
        width: shape.props.w,
        height: shape.props.h,
        opacity: shape.props.transparency,
        position: "relative", // Needed for absolute positioning of image inside
        // QR codes need visible overflow so the CSS drop-shadow filter isn't clipped.
        // Image masks and previews use clip-path on HTMLContainer; everything else hides overflow.
        overflow: isMaskShape || isPreviewShape || isQRCodeShape ? "visible" : "hidden",
        filter: qrDropShadowFilter,
        // Outer drop-shadow lives on THIS wrapper, not on `HTMLContainer`
        // below. Reason: this wrapper sets `overflow: hidden` for normal
        // images, which clips any box-shadow rendered by its descendants
        // (the shadow would be drawn at the HTMLContainer border but
        // immediately cut by the outer clip). An element's OWN box-shadow
        // is NOT clipped by its own overflow:hidden — it's painted
        // outside the border-box — so applying it here makes the shadow
        // actually visible. QR codes keep using `filter: drop-shadow`
        // on this wrapper so the shadow tracks the opaque QR pixels
        // rather than the rectangular bounding box.
        borderRadius: isQRCodeShape ? undefined : radiusCss,
        boxShadow: isQRCodeShape ? undefined : shadows.outShadows,
      }}
      {...(isQRCodeShape ? { "data-qr-code": "true", "data-qr-link": effectiveQrLink } : {})}
    >
      {showCropPreview && loadedSrc && (
        <div style={containerStyle}>
          <img
            className="tl-image"
            style={{
              ...getFlipStyle(shape),
              opacity: 0.5,
              backgroundColor: "transparent",
            }}
            crossOrigin={crossOrigin}
            src={loadedSrc}
            referrerPolicy="strict-origin-when-cross-origin"
            draggable={false}
            {...qrCodeAttributes}
          />
        </div>
      )}

      <HTMLContainer
        key={`${JSON.stringify(outShadowArray)}-${JSON.stringify(inShadowArray)}`}
        id={shape.id}
        style={{
          backgroundImage: "transparent",
          borderRadius: radiusCss,
          // The outer drop-shadow now lives on the `.tl-shape` wrapper
          // above (see comment there). Leaving it here too would
          // double-render — and worse, for normal images it would
          // immediately be clipped by the wrapper's overflow:hidden,
          // which is exactly the bug this refactor fixes.
          boxShadow: undefined,
          // For image masks and previews, DON'T use overflow:hidden - the clip-path handles clipping
          overflow: isMaskShape || isPreviewShape ? "visible" : "hidden",
          // If masked, use original dimensions; if preview, use shape dimensions; otherwise use shape dimensions
          width: imageOffset ? imageOffset.originalWidth : shape.props.w,
          height: imageOffset ? imageOffset.originalHeight : shape.props.h,
          // If masked, apply absolute positioning with offset; if preview, use relative positioning
          position: imageOffset ? ("absolute" as const) : ("relative" as const),
          left: imageOffset ? imageOffset.offsetX : 0,
          top: imageOffset ? imageOffset.offsetY : 0,
          opacity: transparency,
          // Apply clip-path to HTMLContainer (for masks: relative to original image position; for previews: relative to shape bounds)
          ...(shape.meta?.maskClipPath && typeof shape.meta.maskClipPath === "string"
            ? { clipPath: `path('${shape.meta.maskClipPath}')` }
            : {}),
          borderTop:
            isShowBorderTop && !isHiddenBorder
              ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
              : "none",
          borderBottom:
            isShowBorderBottom && !isHiddenBorder
              ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
              : "none",
          borderLeft:
            isShowBorderLeft && !isHiddenBorder
              ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
              : "none",
          borderRight:
            isShowBorderRight && !isHiddenBorder
              ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
              : "none",
        }}
      >
        <div
          style={{
            ...getFlipStyle(shape),
            borderRadius: radiusCss,
            border: isHovered ? `1px solid #3182ed` : "none",
          }}
          className="tl-image absolute top-0 left-0 z-[999999999]"
        />
        <div
          style={{
            ...getFlipStyle(shape),
            boxShadow: `${shadows.inShadows}`,
          }}
          className="tl-image absolute top-0 left-0 z-[9999999999]"
        />
        <div className={classNames("tl-image-container")} style={containerStyle}>
          {/* We have two images: the currently loaded image, and the next image that
					we're waiting to load. we keep the loaded image mounted while we're waiting
					for the next one by storing the loaded URL in state. We use `key` props with
					the src of the image so that when the next image is ready, the previous one will
					be unmounted and the next will be shown with the browser having to remount a
					fresh image and decoded it again from the cache. */}
          {loadedSrc && (
            <img
              key={loadedSrc}
              className="tl-image"
              style={{
                ...getFlipStyle(shape),
                filter: isHiddenLayerBlur ? undefined : `blur(${filterBlur}px)`,
              }}
              crossOrigin={crossOrigin}
              src={loadedSrc}
              referrerPolicy="strict-origin-when-cross-origin"
              draggable={false}
              {...qrCodeAttributes}
            />
          )}
          {nextSrc && (
            <img
              style={{
                ...getFlipStyle(shape),
                filter: isHiddenLayerBlur ? undefined : `blur(${filterBlur}px)`,
              }}
              key={nextSrc}
              crossOrigin={crossOrigin}
              src={nextSrc}
              referrerPolicy="strict-origin-when-cross-origin"
              draggable={false}
              onLoad={() => setLoadedUrl(nextSrc)}
              {...qrCodeAttributes}
            />
          )}
          {/* Optimistic QR background tint — solid fill behind the modules.
              Rendered before (under) the module tint so modules paint on top. */}
          {showQrBgTint && (
            <div
              data-qr-tint-overlay="true"
              aria-hidden="true"
              style={{
                ...getFlipStyle(shape),
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: qrBackgroundColor,
                pointerEvents: "none",
              }}
            />
          )}
          {/* Optimistic QR color tint — see comment near `showQrColorTint`.
              `data-qr-tint-overlay` lets the HTML export strip it (the
              exported <img> already carries the real, regenerated color). */}
          {showQrColorTint && (
            <div
              data-qr-tint-overlay="true"
              aria-hidden="true"
              style={{
                ...getFlipStyle(shape),
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: qrPixelColor,
                // Stretch the mask to fill the box exactly like the <img>
                // underneath (.tl-image is absolute/inset:0, object-fit fill),
                // so the tint lines up even when the QR is resized non-uniformly.
                WebkitMaskImage: `url("${qrTintMaskSrc}")`,
                maskImage: `url("${qrTintMaskSrc}")`,
                WebkitMaskSize: "100% 100%",
                maskSize: "100% 100%",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
        {calculatedFill !== "transparent" && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: calculatedFill,
              borderRadius: radiusCss,
              pointerEvents: "none",
            }}
          />
        )}
        {shape.props.url && <HyperlinkButton url={shape.props.url} />}
      </HTMLContainer>
    </div>
  );
});

function getIsAnimated(editor: Editor, shape: CustomImageUtilProps) {
  const asset = shape.props.assetId ? editor.getAsset(shape.props.assetId as TLAssetId) : undefined;

  if (!asset) return false;

  return (
    ("mimeType" in asset.props && MediaHelpers.isAnimatedImageType(asset?.props.mimeType)) ||
    ("isAnimated" in asset.props && asset.props.isAnimated)
  );
}

/**
 * When an image is cropped we need to translate the image to show the portion withing the cropped
 * area. We do this by translating the image by the negative of the top left corner of the crop
 * area.
 *
 * @param shape - Shape The image shape for which to get the container style
 * @returns - Styles to apply to the image container
 */
function getCroppedContainerStyle(shape: CustomImageUtilProps) {
  const crop = shape.props.crop;
  const topLeft = crop?.topLeft;
  if (!topLeft) {
    return {
      width: shape.props.w,
      height: shape.props.h,
    };
  }

  const w = (1 / (crop.bottomRight.x - crop.topLeft.x)) * shape.props.w;
  const h = (1 / (crop.bottomRight.y - crop.topLeft.y)) * shape.props.h;

  const offsetX = -topLeft.x * w;
  const offsetY = -topLeft.y * h;
  return {
    transform: `translate(${offsetX}px, ${offsetY}px)`,
    width: w,
    height: h,
  };
}

function getFlipStyle(shape: CustomImageUtilProps, size?: { width: number; height: number }) {
  const { flipX, flipY } = shape.props;
  if (!flipX && !flipY) return undefined;

  const scale = `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`;
  const translate = size ? `translate(${flipX ? size.width : 0}px, ${flipY ? size.height : 0}px)` : "";

  return {
    transform: `${translate} ${scale}`,
    // in SVG, flipping around the center doesn't work so we use explicit width/height
    transformOrigin: size ? "0 0" : "center center",
  };
}

function SvgImage({ shape, src }: { shape: CustomImageUtilProps; src: string }) {
  const cropClipId = String(new Date());
  const containerStyle = getCroppedContainerStyle(shape);
  const crop = shape.props.crop;
  if (containerStyle.transform && crop) {
    const { transform: cropTransform, width, height } = containerStyle;
    const croppedWidth = (crop.bottomRight.x - crop.topLeft.x) * width;
    const croppedHeight = (crop.bottomRight.y - crop.topLeft.y) * height;

    const points = [
      new Vec(0, 0),
      new Vec(croppedWidth, 0),
      new Vec(croppedWidth, croppedHeight),
      new Vec(0, croppedHeight),
    ];

    const flip = getFlipStyle(shape, { width, height });

    return (
      <>
        <defs>
          <clipPath id={cropClipId}>
            <polygon points={points.map((p) => `${p.x},${p.y}`).join(" ")} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${cropClipId})`}>
          <image
            href={src}
            width={width}
            height={height}
            style={flip ? { ...flip, transform: `${cropTransform} ${flip.transform}` } : { transform: cropTransform }}
          />
        </g>
      </>
    );
  } else {
    return (
      <image
        href={src}
        width={shape.props.w}
        height={shape.props.h}
        style={getFlipStyle(shape, { width: shape.props.w, height: shape.props.h })}
      />
    );
  }
}
